/**
 * /producao/pedidos/:id — Detalhe do Pedido (visão de produção, agregador).
 *
 * Versão RICA do agregador do brief ("Tela — Detalhe do Pedido"): junta os
 * N ambientes de produção do pedido numa resposta única ("onde está o
 * PV-UN1-1 inteiro?"). Read-mostly — quase tudo aqui DERIVA dos ambientes;
 * nada de estado próprio nem endpoint novo:
 *
 *   - GET /api/ambientes-producao?pedido={id}  → cards + agregações
 *   - GET /api/pedidos/{id}                    → dados comerciais
 *     (enriquecimento: título/situação/valor/datas — a tela degrada para
 *     os dados dos ambientes se o usuário não tiver pedido:view)
 *   - GET /api/producao/atrasos/analise?pedido={id}&periodo=90d&lente=tempo
 *     → KPI de atraso acumulado (kpis.total_min/ocorrencias)
 *
 * Agregações seguem a regra do brief: fase do pedido = MIN das fases (o
 * pedido "está" na fase do ambiente mais atrás); buffer = MAX (pior);
 * NUNCA soma para fase/folga. Adaptações honestas (nada inventado):
 *   - Progresso médio ponderado por pecas_total (fallback média simples
 *     enquanto nenhum ambiente tem peças materializadas).
 *   - Progresso por ambiente exibido em % (o serializer Lista não expõe
 *     contagem de etapas concluídas — mostrar "X/23" seria chute com
 *     etapas não-aplicáveis).
 * Blocos do brief que exigiriam backend novo (folga em dias úteis, duas
 * datas D10, carregar ambiente inline, timeline de eventos, dossiê)
 * ficam para quando o modelo expor os dados.
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiCloudSnow } from "react-icons/fi";
import styled from "styled-components";

import EntityHeader from "../../components/EntityHeader";
import KpiStrip from "../../components/KpiStrip";
import AmbienteCard from "../../components/producao/AmbienteCard";
import { useAuth } from "../../context/AuthContext";
import {
  FASE_INFO,
  analiseAtrasos,
  listarAmbientes,
  obterPedidoComercial,
} from "../../services/producaoV2";
import {
  formatarDataCurta,
  formatarDataHoraCurta,
  formatarHorasMinutos,
} from "../../utils/dataHora";
import { PageContainer } from "../../styles/shared";
import { SITUACAO_TONE_PEDIDO, fmtMoeda } from "../Orcamentos/helpers";
import {
  Card,
  EmptyMsg,
  ErrorMsg,
  Pill,
  SecondaryButton,
  SectionTitle,
} from "../Orcamentos/styles";
import {
  LinhaErro,
  Mono,
  NotaDiscreta,
  Shimmer,
  TabelaFixa,
} from "./_shared/ui";
import { usePodeVerPrecos } from "./_shared/usePodeVerPrecos";

const FASE_CARREGADO = "CARREGADO";
const PERIODO_ATRASOS = "90d";

/** Ordem cronológica das fases (insertion order de FASE_INFO). */
const INDICE_FASE = Object.keys(FASE_INFO).reduce((acc, fase, idx) => {
  acc[fase] = idx;
  return acc;
}, {});

const COR_ZONA = {
  verde: "var(--success-solid)",
  amarelo: "var(--warning-solid)",
  vermelho: "var(--danger-solid)",
};

const TONE_ZONA = {
  verde: "success",
  amarelo: "warning",
  vermelho: "danger",
};

// ---------------------------------------------------------------------------
// Styled
// ---------------------------------------------------------------------------

const GradeAmbientes = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
`;

const DotZona = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 5px;
  vertical-align: middle;
  background: ${({ $zona }) => COR_ZONA[$zona] || COR_ZONA.verde};
`;

/** Barra fina de progresso (accent — progresso não é estado de alerta). */
const BarraProgresso = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;

  .trilho {
    flex: 1 1 auto;
    min-width: 0;
    height: 4px;
    border-radius: 999px;
    background: var(--surface-3);
    overflow: hidden;
  }
  .preenchido {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: var(--accent);
  }
`;

// ---------------------------------------------------------------------------
// Tela
// ---------------------------------------------------------------------------

export default function DetalhePedidoProducao() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { request } = useAuth();
  const podeVerPrecos = usePodeVerPrecos();

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");
  const [ambientes, setAmbientes] = useState([]);
  const [pedido, setPedido] = useState(null);
  const [atrasos, setAtrasos] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro("");
    try {
      // Ambientes são o dado ESSENCIAL (falha vira erro inline + retry);
      // pedido e análise de atrasos são enriquecimento — degradam para
      // null sem derrubar a tela (o KPI mostra "—", o header usa fallback).
      const [respAmbientes, respPedido, respAtrasos] = await Promise.all([
        listarAmbientes(request, { pedido: id }),
        obterPedidoComercial(request, id).catch(() => null),
        analiseAtrasos(request, {
          pedido: id,
          periodo: PERIODO_ATRASOS,
          lente: "tempo",
        }).catch(() => null),
      ]);
      const lista = respAmbientes?.results || respAmbientes || [];
      setAmbientes(Array.isArray(lista) ? lista : []);
      setPedido(respPedido);
      setAtrasos(respAtrasos);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar a produção do pedido.");
    } finally {
      setCarregando(false);
    }
  }, [request, id]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // ---- agregações derivadas dos ambientes ----
  const agregado = useMemo(() => {
    const ordenados = [...ambientes].sort(
      (a, b) => (b.buffer?.pct ?? 0) - (a.buffer?.pct ?? 0),
    );
    const carregados = ambientes.filter(
      (a) => a.fase === FASE_CARREGADO,
    ).length;
    const congelados = ambientes.filter((a) => a.congelado).length;
    const pecasTotais = ambientes.reduce(
      (soma, a) => soma + (Number(a.pecas_total) || 0),
      0,
    );

    // Progresso médio ponderado por peças; sem peça materializada ainda
    // (Σ pesos = 0), cai para a média simples dos percentuais.
    let progressoMedio = null;
    if (ambientes.length > 0) {
      const somaPesos = pecasTotais;
      if (somaPesos > 0) {
        const somaPonderada = ambientes.reduce(
          (soma, a) =>
            soma + (Number(a.progresso_pct) || 0) * (Number(a.pecas_total) || 0),
          0,
        );
        progressoMedio = Math.round(somaPonderada / somaPesos);
      } else {
        const somaSimples = ambientes.reduce(
          (soma, a) => soma + (Number(a.progresso_pct) || 0),
          0,
        );
        progressoMedio = Math.round(somaSimples / ambientes.length);
      }
    }

    // Fase do pedido = MIN das fases (regra do brief: o pedido está na
    // fase do ambiente mais atrás — nunca soma, nunca a mais adiantada).
    let faseMin = null;
    for (const a of ambientes) {
      if (INDICE_FASE[a.fase] === undefined) continue;
      if (faseMin === null || INDICE_FASE[a.fase] < INDICE_FASE[faseMin]) {
        faseMin = a.fase;
      }
    }

    // Pior buffer = MAX de consumo (elo crítico do pedido).
    let piorBuffer = null;
    for (const a of ambientes) {
      const pct = a.buffer?.pct ?? 0;
      if (piorBuffer === null || pct > piorBuffer.pct) {
        piorBuffer = { pct, zona: a.buffer?.zona || "verde", numero: a.numero };
      }
    }

    return {
      ordenados, carregados, congelados, pecasTotais, progressoMedio,
      faseMin, piorBuffer,
    };
  }, [ambientes]);

  const pedidoNumero =
    pedido?.numero || ambientes[0]?.pedido_numero || "";
  const clienteNome =
    pedido?.cliente_nome || ambientes[0]?.cliente_nome || "";
  const semAmbientes = !carregando && !erro && ambientes.length === 0;

  // ---- EntityHeader: badges + subtítulo com os dados comerciais ----
  const badges = [];
  if (pedido?.situacao) {
    badges.push({
      key: "situacao",
      label: pedido.situacao_display || pedido.situacao,
      tone: SITUACAO_TONE_PEDIDO[pedido.situacao] || "neutral",
    });
  }
  if (agregado.faseMin) {
    badges.push({
      key: "fase",
      label: FASE_INFO[agregado.faseMin]?.rotulo || agregado.faseMin,
      tone: "accent",
    });
  }
  if (agregado.congelados > 0) {
    badges.push({
      key: "congelados",
      icon: <FiCloudSnow size={12} aria-hidden="true" />,
      label: `${agregado.congelados} congelado(s)`,
      tone: "warning",
    });
  }

  const subtitulo = useMemo(() => {
    const partes = [];
    if (clienteNome) partes.push(clienteNome);
    if (ambientes.length > 0) {
      partes.push(`${ambientes.length} ambiente(s) em produção`);
    }
    // Gate de preço (regra da gestora): o valor do pedido só entra no
    // subtítulo para quem tem preco:view.
    if (pedido?.valor_total != null && podeVerPrecos) {
      partes.push(fmtMoeda(pedido.valor_total));
    }
    if (pedido?.created_at) {
      partes.push(`entrada ${formatarDataHoraCurta(pedido.created_at)}`);
    }
    if (pedido?.data_entrega_prevista) {
      partes.push(
        `entrega prevista ${formatarDataCurta(pedido.data_entrega_prevista)}`,
      );
    }
    if (pedido?.vendedor_nome) partes.push(`vendedor ${pedido.vendedor_nome}`);
    return partes.join(" · ") || "Ambientes de produção do pedido";
  }, [clienteNome, ambientes.length, pedido, podeVerPrecos]);

  // ---- KPIs ----
  const kpisAtraso = atrasos?.kpis || null;
  const itensKpi = useMemo(() => {
    const semDado = ambientes.length === 0;
    const pior = agregado.piorBuffer;
    return [
      {
        label: "Ambientes",
        value: semDado ? "–" : ambientes.length,
        sub: semDado
          ? "nenhum em produção"
          : `${agregado.carregados} de ${ambientes.length} carregado(s)`,
      },
      {
        label: "Peças",
        value: semDado ? "–" : agregado.pecasTotais,
        sub: "Σ das peças dos ambientes",
      },
      {
        label: "Progresso médio",
        value: agregado.progressoMedio === null
          ? "–"
          : `${agregado.progressoMedio}%`,
        sub: agregado.pecasTotais > 0
          ? "ponderado pelas peças de cada ambiente"
          : "média simples (sem peças materializadas)",
      },
      {
        label: "Pior buffer",
        value: pior ? `${pior.pct}%` : "–",
        sub: pior ? (
          <span title={`Elo crítico: ${pior.numero}`}>
            <DotZona $zona={pior.zona} aria-hidden="true" />
            zona {pior.zona} · {pior.numero}
          </span>
        ) : (
          "sem ambientes no fluxo"
        ),
      },
      {
        label: "Atraso acumulado",
        value: kpisAtraso
          ? formatarHorasMinutos(kpisAtraso.total_min, "0min")
          : "—",
        sub: kpisAtraso
          ? `${kpisAtraso.ocorrencias ?? 0} ocorrência(s) · últimos 90 dias`
          : "análise de atrasos indisponível",
      },
    ];
  }, [ambientes.length, agregado, kpisAtraso]);

  return (
    <PageContainer>
      <EntityHeader
        onBack={() => navigate("/producao")}
        backLabel="Painel"
        title={pedidoNumero ? `Produção — ${pedidoNumero}` : "Produção do pedido"}
        subtitle={subtitulo}
        badges={badges}
        primaryAction={{
          label: "Ficha comercial →",
          onClick: () => navigate(`/pedidos/${id}`),
        }}
      />

      {erro ? (
        <LinhaErro>
          <ErrorMsg style={{ flex: 1 }}>
            Não foi possível carregar a produção do pedido. {erro}
          </ErrorMsg>
          <SecondaryButton type="button" onClick={carregar}>
            Tentar de novo
          </SecondaryButton>
        </LinhaErro>
      ) : carregando ? (
        <>
          <Shimmer $h={92} style={{ marginBottom: 16 }} />
          <Shimmer $h={230} style={{ marginBottom: 16 }} />
          <Shimmer $h={200} />
        </>
      ) : (
        <>
          <KpiStrip items={itensKpi} />

          <Card>
            <SectionTitle>Ambientes de produção</SectionTitle>
            {semAmbientes ? (
              <EmptyMsg>
                Nenhum ambiente de produção para este pedido — eles nascem
                quando o pedido entra no fluxo de produção.
              </EmptyMsg>
            ) : (
              <>
                <GradeAmbientes>
                  {agregado.ordenados.map((a) => (
                    <AmbienteCard
                      key={a.id}
                      densidade="compact"
                      mostrarContexto
                      codigo={a.numero}
                      nome={a.nome}
                      pedidoNumero={a.pedido_numero}
                      cliente={a.cliente_nome}
                      faseLabel={FASE_INFO[a.fase]?.curto || a.fase}
                      pecasTotal={a.pecas_total}
                      progressoPct={a.progresso_pct}
                      bufferPct={a.buffer?.pct ?? 0}
                      bufferZona={a.buffer?.zona || "verde"}
                      congelado={a.congelado}
                      onClick={() => navigate(`/producao/ambientes/${a.id}`)}
                    />
                  ))}
                </GradeAmbientes>
                <NotaDiscreta>
                  ordenado do pior buffer para o melhor — o crítico primeiro.
                </NotaDiscreta>
              </>
            )}
          </Card>

          {!semAmbientes && (
            <Card style={{ marginTop: 16 }}>
              <SectionTitle>Linha do tempo por ambiente</SectionTitle>
              <TabelaFixa>
                <colgroup>
                  <col style={{ width: "30%" }} />
                  <col style={{ width: "16%" }} />
                  <col style={{ width: "26%" }} />
                  <col style={{ width: "14%" }} />
                  <col style={{ width: "14%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Ambiente</th>
                    <th>Fase atual</th>
                    <th>Progresso</th>
                    <th>Buffer</th>
                    <th>Congelado</th>
                  </tr>
                </thead>
                <tbody>
                  {agregado.ordenados.map((a) => {
                    const pct = Number(a.progresso_pct) || 0;
                    const zona = a.buffer?.zona || "verde";
                    return (
                      <tr
                        key={a.id}
                        data-clicavel="true"
                        onClick={() => navigate(`/producao/ambientes/${a.id}`)}
                        title={`${a.numero} · ${a.nome}`}
                      >
                        <td>
                          <Mono>{a.numero}</Mono>
                          {" · "}
                          {a.nome}
                        </td>
                        <td title={FASE_INFO[a.fase]?.rotulo || a.fase_display}>
                          <Pill $tone="purple">
                            {FASE_INFO[a.fase]?.curto || a.fase_display}
                          </Pill>
                        </td>
                        <td>
                          <BarraProgresso
                            title={`${pct}% das etapas aplicáveis concluídas`}
                          >
                            <span className="trilho" aria-hidden="true">
                              <span
                                className="preenchido"
                                style={{ width: `${Math.min(pct, 100)}%` }}
                              />
                            </span>
                            <Mono>{pct}%</Mono>
                          </BarraProgresso>
                        </td>
                        <td>
                          <Pill
                            $tone={TONE_ZONA[zona] || "success"}
                            title={`Consumo de buffer: ${a.buffer?.pct ?? 0}% (zona ${zona})`}
                          >
                            {a.buffer?.pct ?? 0}%
                          </Pill>
                        </td>
                        <td>
                          {a.congelado ? (
                            <Pill $tone="warning">
                              <FiCloudSnow size={11} aria-hidden="true" />
                              Congelado
                            </Pill>
                          ) : (
                            "–"
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </TabelaFixa>
            </Card>
          )}
        </>
      )}
    </PageContainer>
  );
}
